import { getState } from './storage';

const BILINGUAL_BOTH = (id) => id.startsWith('word_') || id === 'greet' || id === 'goodbye';

const MP3_TRACKS = ['theme_garden', 'theme_forest', 'theme_island'];

class AudioManager {
  constructor() {
    this.ready = false;
    this.voiceEl = null;
    this.musicEl = null;
    this.currentTrack = null;
    this.sfxPool = {};
    this._voiceResolve = null;
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.musicEl?.pause();
        else if (this.currentTrack && this.settings().music) this.musicEl?.play().catch(() => {});
      });
    }
  }

  settings() { return getState().settings; }

  init() {
    if (this.ready) return;
    this.ready = true;
    this.voiceEl = new Audio();
    this.musicEl = new Audio();
    this.musicEl.loop = true;
  }

  stopVoice() {
    if (this.voiceEl) { this.voiceEl.pause(); this.voiceEl.src = ''; }
    if (this._voiceResolve) { this._voiceResolve(); this._voiceResolve = null; }
  }

  // Resolves true when the clip actually played, false when the file is
  // missing — callers use this to fall back to an older line rather than
  // leaving a pre-literate child in silence.
  _playFile(src) {
    return new Promise((resolve) => {
      if (!this.voiceEl) return resolve(false);
      let played = false;
      this._voiceResolve = () => resolve(played);
      this.voiceEl.src = src;
      this.voiceEl.onended = () => { this._voiceResolve = null; resolve(true); };
      this.voiceEl.onerror = () => { this._voiceResolve = null; resolve(false); };
      this.voiceEl.play().then(() => { played = true; }).catch(() => { this._voiceResolve = null; resolve(false); });
    });
  }

  // `alt` is an optional older voice key, used only when `id` has no recording
  // yet. This lets new games ship with correct keys before the audio exists.
  async playVoice(id, alt) {
    if (!this.ready || !this.settings().voice) return;
    this.stopVoice();
    this.duck(true);
    const { language, bilingual } = getState().profile;
    const primary = language === 'en' ? 'en' : 'ms';
    let key = id;
    const ok = await this._playFile(`/assets/voice/${primary}/${id}.mp3`);
    if (!ok && alt) {
      key = alt;
      await this._playFile(`/assets/voice/${primary}/${alt}.mp3`);
    }
    if (bilingual && BILINGUAL_BOTH(key)) {
      const other = primary === 'ms' ? 'en' : 'ms';
      await this._playFile(`/assets/voice/${other}/${key}.mp3`);
    }
    this.duck(false);
  }

  playSfx(name) {
    if (!this.ready || !this.settings().sfx) return;
    const ext = name.startsWith('snd_') ? (['snd_cat','snd_dog','snd_bird','snd_car','snd_ball'].includes(name) ? 'mp3' : 'wav') : 'wav';
    const a = new Audio(`/assets/sfx/${name}.${ext}`);
    a.volume = 0.7;
    a.play().catch(() => {});
    return a;
  }

  async playObjectSound(name) {
    if (!this.ready) return;
    const wasPlaying = this.currentTrack;
    this.duck(true);
    return new Promise((resolve) => {
      const ext = ['snd_cat','snd_dog','snd_bird','snd_car','snd_ball'].includes(name) ? 'mp3' : 'wav';
      const a = new Audio(`/assets/sfx/${name}.${ext}`);
      a.volume = 0.85;
      a.onended = () => { this.duck(false); resolve(); };
      a.onerror = () => { this.duck(false); resolve(); };
      a.play().catch(() => { this.duck(false); resolve(); });
    });
  }

  music(track) {
    if (!this.ready) return;
    if (!this.settings().music) { this.stopMusic(); this.currentTrack = track; return; }
    if (this.currentTrack === track && !this.musicEl.paused) return;
    this.currentTrack = track;
    this.musicEl.src = `/assets/music/${track}.${MP3_TRACKS.includes(track) ? 'mp3' : 'wav'}`;
    this.musicEl.volume = this.settings().musicVolume ?? 0.35;
    this.musicEl.play().catch(() => {});
  }

  refreshMusic() {
    if (!this.ready) return;
    if (this.settings().music && this.currentTrack) {
      this.musicEl.src = this.musicEl.src || `/assets/music/${this.currentTrack}.${MP3_TRACKS.includes(this.currentTrack) ? 'mp3' : 'wav'}`;
      this.musicEl.volume = this.settings().musicVolume ?? 0.35;
      this.musicEl.play().catch(() => {});
    } else this.musicEl?.pause();
  }

  stopMusic() { this.musicEl?.pause(); this.currentTrack = null; }

  duck(on) {
    if (!this.musicEl) return;
    this.musicEl.volume = on ? 0.08 : (this.settings().musicVolume ?? 0.35);
  }
}

export const audio = new AudioManager();
